import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LanguageInfo } from '../configuration';
import { ERROR } from '../enums/error.enum';
import { LanguageSelectors } from '../language/language.selectors';
import { NotificationService } from '../services';
import { extractApiErrorMessage, markErrorAsHandledByInterceptor } from '../utils';
import { acceptConfirmOf } from '../utils/accept-confirm.utils';

export const serverErrorInterceptorState = {
  unauthorizedErrorOccurred: false,
};

// Local alias for readability inside the switch below.
const markHandled = markErrorAsHandledByInterceptor;

export const serverErrorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const router = inject(Router);
  const store = inject(Store);
  const translate = inject(TranslateService);
  const notificationService = inject(NotificationService);

  let unauthorizedErrorOccurred = serverErrorInterceptorState.unauthorizedErrorOccurred;

  const languageSnapshot = store.selectSnapshot<LanguageInfo | null>(
    LanguageSelectors.getCurrentLang,
  );

  let clonedRequest = req;

  if (languageSnapshot?.language) {
    clonedRequest = req.clone({
      headers: req.headers.append('Accept-Language', languageSnapshot.language),
    });
  }

  return next(clonedRequest).pipe(
    catchError((err: unknown): Observable<HttpEvent<unknown>> => {
      const error = err instanceof HttpErrorResponse ? err : new HttpErrorResponse({});

      if (
        !isLoginPage(error) &&
        !isHomePage(router) &&
        !isNotifications(error) &&
        !isGlobalNotifications(error) &&
        !isCalculationLineDetail(error)
      ) {
        switch (error.status) {
          case ERROR.BAD_REQUEST:
            if (!acceptConfirmOf(error)) {
              displayServerErrorMessage(notificationService, translate, error, router, req.url);
            }
            markHandled(error);
            break;

          case ERROR.UNAUTHORIZED:
            unauthorizedErrorOccurred = true;
            serverErrorInterceptorState.unauthorizedErrorOccurred = true;

            displayForbiddenMessage(notificationService, translate, true, undefined, router);
            markHandled(error);
            break;

          case ERROR.FORBIDDEN:
          case ERROR.SERVICE_UNAVAILABLE:
            if (!unauthorizedErrorOccurred) {
              displayServerErrorMessage(notificationService, translate, error, router);
              markHandled(error);
            }
            break;

          case ERROR.LARGE_REQUEST:
            break;
        }
      }

      return throwError((): HttpErrorResponse => error);
    }),
  );
};

/**
 * A download asks for `responseType: 'blob'`, so a failed one arrives with a body
 * nothing can read synchronously, and `extractApiErrorMessage` — finding none of
 * the shapes it knows — falls through to Angular's own message: "Http failure
 * response for https://…: 400 Bad Request". Read the blob and the text inside it
 * is the same JSON body every other path already handles.
 */
function displayServerErrorMessage(
  notificationService: NotificationService,
  translate: TranslateService,
  error: HttpErrorResponse,
  router: Router,
  url?: string,
): void {
  const show = (message?: string): void =>
    displayForbiddenMessage(notificationService, translate, false, message, router);

  if (error.error instanceof Blob) {
    void error.error.text().then((text): void => show(messageFromText(text)));

    return;
  }

  show(getExtractedErrorMessage(error, url));
}

/** A body the backend sent as text: its JSON message if it is JSON, itself if not. */
function messageFromText(text: string): string | undefined {
  try {
    return (extractApiErrorMessage(JSON.parse(text)) ?? text.trim()) || undefined;
  } catch {
    return text.trim() || undefined;
  }
}

function getExtractedErrorMessage(error: HttpErrorResponse, url?: string): string | undefined {
  if (url && url.includes('token')) return;

  const defaultErrorMessage = 'Error occurred, please contact administrator!';

  // Some endpoints return their body as a JSON string instead of a parsed
  // object — handle that one case here, then let `extractApiErrorMessage`
  // do the heavy lifting for everything else (object body / nested wrappers
  // / per-field `errors` maps). Single source of truth across the platform.
  const backendErr: unknown = error.error;
  if (typeof backendErr === 'string') {
    return messageFromText(backendErr);
  }

  return extractApiErrorMessage(error) ?? defaultErrorMessage;
}

function isLoginPage({ url }: HttpErrorResponse): boolean | undefined {
  return url?.includes('/login');
}

function isHomePage(router: Router): boolean | undefined {
  return router.url.includes('/home');
}

function isNotifications({ url }: HttpErrorResponse): boolean | undefined {
  return url?.includes('/Notifications');
}

// The GlobalNotifications settings CRUD surfaces its own errors at the
// component level (it needs to show the API `title` for validation failures),
// so — like the `/Notifications` bell endpoint — the interceptor defers here
// instead of toasting a second, more technical message.
function isGlobalNotifications({ url }: HttpErrorResponse): boolean | undefined {
  return url?.includes('/GlobalNotifications');
}

// Calculation-line detail CRUD (Create/Update) toasts a per-field formatted
// error message from `configuration-line-detail-sidenav.component.ts` via its
// NGXS action subscription. Interceptor defers so users don't get two toasts
// for the same 400.
function isCalculationLineDetail({ url }: HttpErrorResponse): boolean | undefined {
  return url?.includes('CalculationLineDetail');
}

function displayForbiddenMessage(
  notificationService: NotificationService,
  translate: TranslateService,
  redirectToHome: boolean,
  message?: string,
  router?: Router,
): void {
  const msg = message || translate.instant('error.forbidden');
  const snackBarRef = notificationService.showError(msg, 5000);

  if (redirectToHome) {
    snackBarRef.afterDismissed().subscribe((): void => {
      router?.navigate(['/home']);
    });
  }
}
